import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../Helpers/ToastHelper';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../Common/Paths';
import { useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { Card, Button, FloatLabel, InputText, ProgressBar, DDCountries, PasswordField, Controller, Path, SubmitHandler } from "../../Common/Imports";
import { ApiResponse, Countries_EXT, DDModel } from '../../Models/Classes';
import { ValidateEmail } from '../../Common/Utility/ValidateEmail';
import { useAxios } from '../../Helpers/AxiosHelper';

interface FormData {
    CompanyName: string | undefined;
    VAT: string | undefined;
    Phone: string | undefined;
    CompanyEmail: string | undefined;
    Address: string | undefined;
    City: string | undefined;
    ZIP: string | undefined;
    Region: string | undefined;
    UserName: string | undefined;
    UserSurname: string | undefined;
    UserEmail: string | undefined;
    Password: string | undefined;
    ConfirmPassword: string | undefined;
    IdCountry: number | undefined;
}

const Register: React.FC = () => {
    const AxiosHelper = useAxios();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        watch,
    } = useForm<FormData>();

    const [RegisterSuccess, setRegisterSuccess] = useState<boolean>(false);

    const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
        setShowLoader(true);
        try {
            await AxiosHelper.post<ApiResponse<object>>(`Auth/Register`, data);
            setRegisterSuccess(true);
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                showToast('error', 'Error', "error");
            } else {
                showToast('error', 'Error', "error");
            }
        }
        finally {
            setShowLoader(false);
        }
    };

    const NavigateTo = (path: string) => {
        navigate(Paths[path].value);
    };

    const footerTemplate = () => (
        <>
            {RegisterSuccess !== true &&
                <div className="row">
                    <div className="col">
                        <Button type="submit" label={t("Auth.Register.Button")} />
                    </div>
                </div>
            }
            <div className="row">
                <div className="col">
                    <a onClick={() => NavigateTo('Login')} className="text-primary secondary-button cursor-pointer">
                        {t("Auth.Register.Link")}
                    </a>
                </div>
            </div>
        </>
    );

    const password = watch('Password');

    const renderInputField = (
        name: keyof FormData,
        label: string,
        validateFunc?: (value: string | number | DDModel<Countries_EXT> | undefined) => boolean | string
    ) => (
        <Controller
            name={name}
            control={control}
            rules={{
                required: `${label} ${t("Auth.Register.Form.Required")}`,
                validate: validateFunc
            }}
            render={({ field, fieldState }) => (
                <div className="mb-4">
                    <FloatLabel>
                        <InputText
                            id={name}
                            {...field}
                            value={field.value ? String(field.value) : ''}  // Convert value to string
                            className={classNames({ 'p-invalid': fieldState.invalid })}
                        />
                        <label htmlFor={name}>{label}</label>
                    </FloatLabel>
                    {fieldState.error && (
                        <small className="p-error">
                            {fieldState.error.message}
                        </small>
                    )}
                </div>
            )}
        />
    );

    return (
        <div className="z-5 w-full lg:w-8 px-6 text-center mt-2" style={{ minWidth: '90vw' }}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                <Card title={t("Auth.Register.Header")} footer={footerTemplate} >
                    {showLoader ? (
                        <>
                            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                        </>
                    ) : (
                        RegisterSuccess === true ?
                            <div className="row">
                                <div className="col">
                                    <h5>{t("Auth.Register.Success")}</h5>
                                </div>
                            </div>
                            :
                            <div className="row">
                                <div className="col" style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "65vh" }}>
                                    <div className="row">
                                        <div className="col">
                                            <h4>{t("Auth.Register.Form.Company.Header")}</h4>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            {renderInputField('CompanyName', t("Auth.Register.Form.Company.Name"))}
                                        </div>
                                        <div className="col-md-6">
                                            {renderInputField('VAT', t("Auth.Register.Form.Company.VAT"))}
                                        </div>
                                        <div className="col-md-6">
                                            {renderInputField('Phone', t("Auth.Register.Form.Company.Phone"))}
                                        </div>
                                        <div className="col-md-6">
                                            {renderInputField(
                                                'CompanyEmail',
                                                t("Auth.Register.Form.Company.Email"),
                                                (value) => value && ValidateEmail(value as string) || t("Auth.Register.Form.Company.Email") + " " + t("Common.Form.Invalid")
                                            )}
                                        </div>
                                        <div className="col-md-12">
                                            {renderInputField('Address', t("Auth.Register.Form.Company.Address"))}
                                        </div>
                                        <div className="col-md-3">
                                            {renderInputField('City', t("Auth.Register.Form.Company.City"))}
                                        </div>
                                        <div className="col-md-3">
                                            {renderInputField('ZIP', t("Auth.Register.Form.Company.ZIP"))}
                                        </div>
                                        <div className="col-md-3">
                                            {renderInputField('Region', t("Auth.Register.Form.Company.Region"))}
                                        </div>
                                        <div className="col-md-3">
                                            <Controller
                                                name="IdCountry"
                                                control={control}
                                                rules={{ required: `${t("Stakeholders.Country")} ${t("Common.Form.Required")}` }}
                                                render={({ field, fieldState }) => (
                                                    <div className="mb-4">
                                                        <DDCountries
                                                            value={field.value}  // Ensure correct type
                                                            onChange={(value) => field.onChange(value)}
                                                            onBlur={field.onBlur}
                                                            valid={fieldState.invalid}
                                                        />
                                                        {fieldState.invalid && (
                                                            <small className="p-error">
                                                                {fieldState.error?.message}
                                                            </small>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <h4>{t("Auth.Register.Form.User.Header")}</h4>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            {renderInputField('UserName', t("Auth.Register.Form.User.Name"))}
                                        </div>
                                        <div className="col-md-6">
                                            {renderInputField('UserSurname', t("Auth.Register.Form.User.Surname"))}
                                        </div>
                                        <div className="col-md-4">
                                            {renderInputField(
                                                'UserEmail',
                                                t("Auth.Register.Form.User.Email"),
                                                (value) => value && ValidateEmail(value as string) || t("Auth.Register.Form.Company.Email") + " " + t("Common.Form.Invalid")
                                            )}
                                        </div>
                                        <div className="col-md-4">
                                            <PasswordField<FormData> control={control} name="Password" label={t("Auth.Register.Form.User.Password")} />
                                        </div>
                                        <div className="col-md-4">
                                            <PasswordField<FormData>
                                                control={control}
                                                name="ConfirmPassword"
                                                label={t("Auth.Register.Form.User.PasswordRepeat")}
                                                validationRules={{
                                                    validate: (value: string | number | undefined) => {
                                                        if (typeof value !== 'string') {
                                                            return t("Auth.Register.Form.User.PasswordRepeat") + " " + t("Auth.Register.Form.Mismatch");
                                                        }
                                                        return value === password || t("Auth.Register.Form.User.PasswordRepeat") + " " + t("Auth.Register.Form.Mismatch");
                                                    },
                                                    deps: ['ConfirmPassword'] as Path<FormData>[]
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            {t("Auth.Register.Form.PasswordInfo")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                    )}
                </Card>
            </form>
        </div>
    );
};

export default Register;
